import { useState, useEffect } from 'react';

// ── Shared constants ───────────────────────────────────────────────────────

export const MKT_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
};

// SP-API Finances returns amounts in marketplace local currency.
export const MKT_CURRENCY: Record<string, string> = {
  ATVPDKIKX0DER: 'USD', A2EUQ1WTGCTBG2: 'CAD', A1AM78C64UM0Y8: 'MXN',
  A1F83G8C2ARO7P: 'GBP', A1PA6795UKMFR9: 'EUR', A13V1IB3VIYZZH: 'EUR',
  APJ6JRA9NG5V4: 'EUR', A1RKKUPIHCS9HS: 'EUR', A1805IZSGTT6HS: 'EUR',
  A2NODRKZP88ZB9: 'SEK',
};

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', CAD: 'C$', MXN: 'MX$', GBP: '£', EUR: '€', SEK: 'kr',
};

export type Period = '7' | '14' | '30' | '90' | 'this-month' | 'last-month' | 'last-quarter' | 'ytd' | 'custom';

// ── Filter state hook ──────────────────────────────────────────────────────

export interface ProductGroup {
  parentAsin: string;
  title: string;
  children: Array<{ sku: string; asin: string; label: string }>;
}

export interface FinanceFilterState {
  period: Period;
  setPeriod: (p: Period) => void;
  marketplace: string;
  setMarketplace: (m: string) => void;
  markets: string[];
  setMarkets: (m: string[]) => void;
  currency: 'usd' | 'local';
  setCurrency: (c: 'usd' | 'local') => void;
  /** Currency param string for API calls: '' or '&currency=local' */
  currencyParam: string;
  /** Format monetary value with appropriate symbol */
  fmt: (val: number) => string;
  /** Label for current currency mode */
  currencyLabel: string;
  /** Date range */
  startDate: string;
  endDate: string;
  setStartDate: (d: string) => void;
  setEndDate: (d: string) => void;
  /** Product filter */
  selectedProduct: string; // parentAsin or empty
  selectedSku: string; // sku or empty
  setSelectedProduct: (p: string) => void;
  setSelectedSku: (s: string) => void;
  products: ProductGroup[];
  setProducts: (p: ProductGroup[]) => void;
  /** Combined date+product param string for API calls */
  dateParam: string;
  productParam: string;
}

function daysAgo(n: number) { return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10); }

export function useFinanceFilters(defaultPeriod: Period = '30'): FinanceFilterState {
  const [period, setPeriodRaw] = useState<Period>(defaultPeriod);
  const [marketplace, setMarketplace] = useState('');
  const [markets, setMarkets] = useState<string[]>([]);
  const [currency, setCurrency] = useState<'usd' | 'local'>('usd');
  const [startDate, setStartDate] = useState(daysAgo(Number(defaultPeriod)));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedSku, setSelectedSku] = useState('');
  const [products, setProducts] = useState<ProductGroup[]>([]);

  // Sync dates when period changes
  function setPeriod(p: Period) {
    setPeriodRaw(p);
    const today = new Date();
    if (p === 'this-month') {
      setStartDate(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
      setEndDate(today.toISOString().slice(0, 10));
    } else if (p === 'last-month') {
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
      setStartDate(lastMonth.toISOString().slice(0, 10));
      setEndDate(lastDay.toISOString().slice(0, 10));
    } else if (p === 'last-quarter') {
      const curQ = Math.floor(today.getMonth() / 3);
      const prevQ = curQ === 0 ? 3 : curQ - 1;
      const prevYear = curQ === 0 ? today.getFullYear() - 1 : today.getFullYear();
      setStartDate(new Date(prevYear, prevQ * 3, 1).toISOString().slice(0, 10));
      setEndDate(new Date(prevYear, prevQ * 3 + 3, 0).toISOString().slice(0, 10));
    } else if (p === 'ytd') {
      setStartDate(new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10));
      setEndDate(today.toISOString().slice(0, 10));
    } else if (p !== 'custom') {
      setStartDate(daysAgo(Number(p)));
      setEndDate(today.toISOString().slice(0, 10));
    }
  }

  // Reset currency to USD when switching to "All" marketplaces
  useEffect(() => {
    if (!marketplace) setCurrency('usd');
  }, [marketplace]);

  // Reset child SKU when product changes
  useEffect(() => {
    setSelectedSku('');
  }, [selectedProduct]);

  const currencyParam = currency === 'local' && marketplace ? '&currency=local' : '';
  const dateParam = `&startDate=${startDate}&endDate=${endDate}`;
  const productParam = selectedSku ? `&sku=${selectedSku}` : selectedProduct ? `&parentAsin=${selectedProduct}` : '';

  const localCur = marketplace ? (MKT_CURRENCY[marketplace] || 'USD') : 'USD';
  const sym = currency === 'local' && marketplace
    ? (CURRENCY_SYMBOL[localCur] || '$')
    : '$';

  const currencyLabel = currency === 'local' && marketplace
    ? localCur
    : 'USD';

  function fmt(val: number): string {
    const abs = Math.abs(val);
    const formatted = abs >= 1000
      ? abs.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${val < 0 ? '-' : ''}${sym}${formatted}`;
  }

  return {
    period, setPeriod, marketplace, setMarketplace, markets, setMarkets,
    currency, setCurrency, currencyParam, fmt, currencyLabel,
    startDate, endDate, setStartDate, setEndDate,
    selectedProduct, selectedSku, setSelectedProduct, setSelectedSku,
    products, setProducts, dateParam, productParam,
  };
}

// ── Filter bar component ───────────────────────────────────────────────────

interface FinanceFilterBarProps {
  filters: FinanceFilterState;
  showPeriod?: boolean;
  showMarketplace?: boolean;
  showCurrency?: boolean;
  showProduct?: boolean;
  children?: React.ReactNode;
}

export function FinanceFilterBar({ filters, showPeriod = true, showMarketplace = true, showCurrency = true, showProduct = false, children }: FinanceFilterBarProps) {
  const { period, setPeriod, marketplace, setMarketplace, markets, currency, setCurrency,
    selectedProduct, selectedSku, setSelectedProduct, setSelectedSku, products } = filters;

  const activeProductGroup = selectedProduct ? products.find(p => p.parentAsin === selectedProduct) : null;

  const btnActive = 'bg-brand-600 text-white';
  const btnInactive = 'bg-gray-100 text-gray-600 hover:bg-gray-200';
  const btnCls = (active: boolean) => `rounded-full px-3 py-1 text-xs font-medium transition ${active ? btnActive : btnInactive}`;

  return (
    <div className="space-y-2">
      {/* Row 1: Marketplace + Currency + Date range + Period */}
      <div className="flex flex-wrap items-center gap-3">
        {showMarketplace && markets.length > 1 && (
          <div className="flex items-center gap-1">
            <button onClick={() => setMarketplace('')} className={btnCls(!marketplace)}>All</button>
            {markets.map(m => (
              <button key={m} onClick={() => setMarketplace(m)} className={btnCls(marketplace === m)}>
                {MKT_LABELS[m] ?? m}
              </button>
            ))}
          </div>
        )}

        {showCurrency && marketplace && (
          <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
            <button onClick={() => setCurrency('usd')}
              className={`px-2.5 py-1 text-xs font-medium transition ${currency === 'usd' ? btnActive : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              USD
            </button>
            <button onClick={() => setCurrency('local')}
              className={`px-2.5 py-1 text-xs font-medium transition ${currency === 'local' ? btnActive : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {MKT_CURRENCY[marketplace] || 'Local'}
            </button>
          </div>
        )}

        {showPeriod && (
          <div className="flex items-center gap-1 ml-auto flex-wrap">
            {([
              { key: '7' as Period, label: '7d' },
              { key: '14' as Period, label: '14d' },
              { key: '30' as Period, label: '30d' },
              { key: '90' as Period, label: '90d' },
              { key: 'this-month' as Period, label: 'MTD' },
              { key: 'last-month' as Period, label: 'Last Month' },
              { key: 'last-quarter' as Period, label: 'Last Q' },
              { key: 'ytd' as Period, label: 'YTD' },
              { key: 'custom' as Period, label: 'Custom' },
            ]).map(({ key, label }) => (
              <button key={key} onClick={() => setPeriod(key)}
                className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
                  period === key ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}>{label}</button>
            ))}
            {period === 'custom' && (
              <div className="flex items-center gap-1 ml-1">
                <input type="date" value={filters.startDate} onChange={e => filters.setStartDate(e.target.value)}
                  className="rounded border border-gray-200 px-2 py-0.5 text-xs" />
                <span className="text-xs text-gray-400">–</span>
                <input type="date" value={filters.endDate} onChange={e => filters.setEndDate(e.target.value)}
                  className="rounded border border-gray-200 px-2 py-0.5 text-xs" />
              </div>
            )}
          </div>
        )}

        {children}
      </div>

      {/* Row 2: Product buttons */}
      {showProduct && products.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-gray-400 mr-1">Product:</span>
          <button onClick={() => setSelectedProduct('')} className={btnCls(!selectedProduct)}>All</button>
          {products.map(p => (
            <button key={p.parentAsin} onClick={() => setSelectedProduct(p.parentAsin === selectedProduct ? '' : p.parentAsin)}
              className={btnCls(selectedProduct === p.parentAsin)}>
              {p.title || p.parentAsin}
            </button>
          ))}
        </div>
      )}

      {/* Row 3: Variation buttons (child ASINs) */}
      {showProduct && activeProductGroup && activeProductGroup.children.length > 1 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-gray-400 mr-1">Variation:</span>
          <button onClick={() => setSelectedSku('')} className={btnCls(!selectedSku)}>All</button>
          {activeProductGroup.children.map(ch => (
            <button key={ch.sku} onClick={() => setSelectedSku(ch.sku === selectedSku ? '' : ch.sku)}
              className={btnCls(selectedSku === ch.sku)}>
              {ch.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
